import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiFolder, FiFile, FiChevronRight, FiChevronDown, FiSettings } from 'react-icons/fi';
import { NodeStatus } from '../../types/artifactory';
import { useArtifactoryStore } from '../../stores/artifactoryStore';

interface FileTreeProps {
  manifest: any;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
}

export const FileTree: React.FC<FileTreeProps> = ({ manifest, selectedNodeId, onSelectNode }) => {
  const { darkMode } = useTheme();
  const { expandedDirs, toggleDir, expandPathToNode } = useArtifactoryStore();

  // When manifest changes or selected node changes, ensure path to selected node is expanded
  useEffect(() => {
    if (manifest && selectedNodeId && manifest.nodes[selectedNodeId]) {
      const nodePath = manifest.nodes[selectedNodeId].path;
      expandPathToNode(nodePath);
    }
  }, [manifest, selectedNodeId]); // Remove expandPathToNode from dependencies
  
  // Track initial expansion state
  const initialExpansionDone = useRef(false);

  // Auto-expand root directories on initial load and when manifest changes
  useEffect(() => {
    if (manifest && manifest.nodes) {
      // Reset expansion state when manifest changes
      if (initialExpansionDone.current && Object.keys(manifest.nodes).length > 0) {
        console.log("Manifest changed, resetting expansion state");
        initialExpansionDone.current = false;
      }
      
      if (!initialExpansionDone.current) {
        console.log("Auto-expanding directories for better visibility");
        
        // Auto-expand the root directory
        const rootDirs = Object.values(manifest.nodes)
          .filter(node => node.type === 'directory' && node.path.split('/').length <= 2)
          .map(node => node.path);
        
        console.log("Root directories to expand:", rootDirs);
        
        // Expand all root directories
        rootDirs.forEach(path => {
          expandPathToNode(path);
        });
        
        // Expand up to 3 levels deep for better visibility
        for (let level = 2; level <= 3; level++) {
          Object.values(manifest.nodes)
            .filter(node => node.type === 'directory' && node.path.split('/').length === level)
            .map(node => node.path)
            .forEach(path => {
              expandPathToNode(path);
            });
        }
        
        // If we have a selected node, make sure its path is expanded
        if (selectedNodeId && manifest.nodes[selectedNodeId]) {
          const nodePath = manifest.nodes[selectedNodeId].path;
          console.log("Expanding path to selected node:", nodePath);
          expandPathToNode(nodePath);
        }
        
        // Mark initial expansion as done
        initialExpansionDone.current = true;
      }
    }
  }, [manifest, selectedNodeId]); // Only depend on manifest and selectedNodeId changes

  if (!manifest) return null;

  // Build directory structure
  const buildDirStructure = () => {
    const dirStructure: any = { '/': { type: 'directory', children: {}, nodeId: null } };
    
    if (!manifest || !manifest.nodes) {
      console.warn("Manifest has no nodes property");
      return dirStructure;
    }
    
    // First pass: create all directories
    Object.values(manifest.nodes).forEach((node: any) => {
      if (!node || !node.path || typeof node.path !== 'string') {
        console.warn("Invalid node or missing path:", node);
        // Skip this node but don't return from the whole function
        return; // Skip this iteration in the forEach
      }
      
      try {
        const parts = node.path.split('/').filter(Boolean);
        let currentPath = '';
        let current = dirStructure['/'].children;
        
        // Create directory structure
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          currentPath += '/' + part;
          
          if (!current[part]) {
            current[part] = { 
              type: 'directory', 
              children: {},
              path: currentPath,
              nodeId: null // We'll set this if we find a directory node
            };
          }
          
          current = current[part].children;
        }
      } catch (error) {
        console.error("Error processing node for directory structure:", error, node);
      }
    });
    
    // Second pass: add files and set directory nodeIds
    Object.values(manifest.nodes).forEach((node: any) => {
      if (!node || !node.path || typeof node.path !== 'string') return;
      
      try {
        const parts = node.path.split('/').filter(Boolean);
        
        if (parts.length === 0) return; // Skip empty paths
        
        if (node.type === 'directory') {
          // This is a directory node, find it in our structure and set its nodeId
          let current = dirStructure['/'].children;
          let currentPath = '';
          
          for (const part of parts) {
            currentPath += '/' + part;
            if (current[part]) {
              current[part].nodeId = node.id;
              current[part].status = node.status;
            }
            if (current[part] && current[part].children) {
              current = current[part].children;
            } else {
              break;
            }
          }
        } else {
          // This is a file node
          const fileName = parts[parts.length - 1];
          let current = dirStructure['/'].children;
          
          // Navigate to the parent directory
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!current[part]) {
              // Create the parent directory if it doesn't exist
              current[part] = { 
                type: 'directory', 
                children: {},
                path: '/' + parts.slice(0, i+1).join('/'),
                nodeId: null
              };
            }
            current = current[part].children;
          }
          
          // Add the file
          current[fileName] = { 
            type: 'file', 
            nodeId: node.id,
            status: node.status,
            path: node.path
          };
        }
      } catch (error) {
        console.error("Error processing node for file structure:", error, node);
      }
    });
    
    return dirStructure;
  };

  const dirStructure = buildDirStructure();

  // Recursive function to render tree
  const renderTree = (structure: any, path: string = '/') => {
    return Object.entries(structure).map(([name, info]: [string, any]) => {
      if (name === '/') {
        // Render root's children
        return (
          <div key="root" className="mt-2">
            {Object.entries(info.children).map(([childName, childInfo]: [string, any]) => 
              renderNode(childName, childInfo, `/${childName}`)
            )}
          </div>
        );
      } else {
        return renderNode(name, info, path + '/' + name);
      }
    });
  };

  // Render a single node (file or directory)
  const renderNode = (name: string, info: any, fullPath: string) => {
    if (info.type === 'file') {
      return (
        <div 
          key={info.nodeId || fullPath}
          className={`pl-6 py-1 flex items-center cursor-pointer ${
            selectedNodeId === info.nodeId 
              ? darkMode 
                ? 'bg-blue-900 bg-opacity-50' 
                : 'bg-blue-100'
              : 'hover:bg-opacity-50 ' + (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
          }`}
          onClick={() => info.nodeId && onSelectNode(info.nodeId)}
        >
          <FiFile className={`mr-2 ${getStatusColor(info.status)}`} />
          <span className="truncate">{name}</span>
        </div>
      );
    } else {
      const isExpanded = expandedDirs.has(fullPath);
      // Determine if this is a root node by checking if it's a top-level directory
      const isRootNode = info.nodeId && manifest && manifest.nodes && 
        !Object.values(manifest.nodes).some(node => 
          node.type === 'directory' && 
          node.path !== manifest.nodes[info.nodeId].path && 
          manifest.nodes[info.nodeId].path.startsWith(node.path + '/')
        );
      
      return (
        <div key={info.nodeId || fullPath}>
          <div 
            className={`py-1 flex items-center cursor-pointer ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            } ${
              isRootNode && selectedNodeId === info.nodeId
                ? darkMode 
                  ? 'bg-blue-900 bg-opacity-50' 
                  : 'bg-blue-100'
                : ''
            }`}
            onClick={() => {
              if (isRootNode && info.nodeId) {
                // If it's the root node and has a nodeId, select it
                onSelectNode(info.nodeId);
              } else {
                // Otherwise toggle directory expansion
                toggleDir(fullPath);
              }
            }}
          >
            {isExpanded ? <FiChevronDown className="mr-1" /> : <FiChevronRight className="mr-1" />}
            <FiFolder className={`mr-2 ${isRootNode ? 'text-blue-500' : 'text-yellow-500'}`} />
            <span 
              className="font-medium truncate"
              title={isRootNode ? "Project Root (Click for project tools)" : ""}
            >
              {name}
            </span>
          </div>
          
          {isExpanded && info.children && Object.keys(info.children).length > 0 && (
            <div className="ml-4">
              {Object.entries(info.children).map(([childName, childInfo]: [string, any]) => 
                renderNode(childName, childInfo, `${fullPath}/${childName}`)
              )}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="p-2 overflow-y-auto h-full min-h-0">
      {renderTree(dirStructure)}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status) {
    case NodeStatus.GHOST: return 'text-gray-400';
    case NodeStatus.PENDING: return 'text-yellow-500';
    case NodeStatus.HYDRATING: return 'text-blue-500';
    case NodeStatus.FRESH: return 'text-green-500';
    case NodeStatus.STALE: return 'text-orange-500';
    case NodeStatus.LOCKED: return 'text-red-500';
    default: return 'text-gray-400';
  }
};
